(() => {
  const normalize = (value) => String(value || "")
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  const pushEvent = (event, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...detail });
  };

  const initGiftFinder = (finder) => {
    if (finder.dataset.initialized === "true") return;
    finder.dataset.initialized = "true";

    const steps = Array.from(finder.querySelectorAll("[data-gift-step]"));
    const progress = Array.from(finder.querySelectorAll("[data-progress-segment]"));
    const count = finder.querySelector("[data-step-count]");
    const results = finder.querySelector("[data-gift-results]");
    const empty = finder.querySelector("[data-gift-empty]");
    const resultGrid = finder.querySelector("[data-result-grid]");
    const resultTitle = finder.querySelector("[data-gift-results-title]");
    const fallbackNote = finder.querySelector("[data-gift-fallback-note]");
    const selectedList = finder.querySelector("[data-selected-list]");
    const allResultsLink = finder.querySelector("[data-all-results-link]");
    const catalog = finder.closest(".bsk-guide-page")?.querySelector("[data-gift-catalog]");
    const catalogItems = catalog ? Array.from(catalog.querySelectorAll("[data-gift-product]")) : [];
    const uniqueCatalogItems = Array.from(new Map(
      catalogItems
        .filter((item) => item.dataset.handle)
        .map((item) => [item.dataset.handle, item])
    ).values());
    const state = {};
    let activeStep = 0;
    let started = false;
    let refreshStepOptions = () => {};

    const setStep = (index) => {
      activeStep = Math.max(0, Math.min(index, steps.length - 1));
      refreshStepOptions(activeStep);
      steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== activeStep;
      });
      progress.forEach((segment, segmentIndex) => {
        segment.classList.toggle("is-active", segmentIndex <= activeStep);
      });
      if (count) count.textContent = `Step ${activeStep + 1} of ${steps.length}`;
      results.hidden = true;
      empty.hidden = true;
    };

    const reset = () => {
      Object.keys(state).forEach((key) => delete state[key]);
      finder.querySelectorAll("[data-gift-value]").forEach((button) => {
        button.setAttribute("aria-pressed", "false");
      });
      started = false;
      setStep(0);
      const url = new URL(window.location.href);
      ["gift_occasion", "gift_format", "gift_budget", "gift_personalized"].forEach((key) => {
        url.searchParams.delete(key);
      });
      window.history.replaceState({}, "", url);
    };

    const updateUrl = () => {
      const url = new URL(window.location.href);
      const map = {
        occasion: "gift_occasion",
        format: "gift_format",
        budget: "gift_budget",
        personalized: "gift_personalized"
      };
      Object.entries(map).forEach(([stateKey, queryKey]) => {
        if (state[stateKey]?.value) {
          url.searchParams.set(queryKey, state[stateKey].value);
        } else {
          url.searchParams.delete(queryKey);
        }
      });
      window.history.replaceState({}, "", url);
    };

    const clearStepsAfter = (index) => {
      steps.forEach((step, stepIndex) => {
        if (stepIndex <= index) return;
        delete state[step.dataset.giftStep];
        step.querySelectorAll("[data-gift-value]").forEach((button) => {
          button.setAttribute("aria-pressed", "false");
        });
      });
    };

    const hasOverride = (overrides, key) => Object.prototype.hasOwnProperty.call(overrides, key);

    const productMatches = (item, ignored = {}, overrides = {}) => {
      const occasion = hasOverride(overrides, "occasion") ? overrides.occasion : state.occasion?.value;
      const format = hasOverride(overrides, "format") ? overrides.format : state.format?.value;
      const budgetMin = hasOverride(overrides, "budgetMin") ? overrides.budgetMin : state.budget?.min;
      const budgetMax = hasOverride(overrides, "budgetMax") ? overrides.budgetMax : state.budget?.max;
      const personalized = hasOverride(overrides, "personalized")
        ? overrides.personalized
        : state.personalized?.value;
      const itemOccasions = normalize(item.dataset.occasions);
      const itemFormat = normalize(item.dataset.format);
      const price = Number(item.dataset.price || 0);
      const canPersonalize = item.dataset.personalizable === "true";

      if (!ignored.occasion && occasion && occasion !== "any" && !itemOccasions.includes(occasion)) return false;
      if (!ignored.format && format && format !== "any" && !itemFormat.includes(format)) return false;
      if (!ignored.budget && Number.isFinite(budgetMin) && price < budgetMin) return false;
      if (!ignored.budget && Number.isFinite(budgetMax) && price > budgetMax) return false;
      if (!ignored.personalized && personalized === "yes" && !canPersonalize) return false;
      if (!ignored.personalized && personalized === "no" && canPersonalize) return false;
      return true;
    };

    const collectMatches = (ignored = {}, overrides = {}) => uniqueCatalogItems.filter(
      (item) => productMatches(item, ignored, overrides)
    );

    const locale = finder.dataset.locale || document.documentElement.lang || "en";
    const currency = finder.dataset.currency || window.Shopify?.currency?.active || "USD";
    const formatPrice = (price) => new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: price % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(price / 100);

    const buildBudgetBands = (items) => {
      const groups = Array.from(items.reduce((map, item) => {
        const price = Number(item.dataset.price || 0);
        if (!map.has(price)) map.set(price, []);
        map.get(price).push(item);
        return map;
      }, new Map()).entries())
        .map(([price, products]) => ({ price, count: products.length }))
        .sort((left, right) => left.price - right.price);

      if (!groups.length) return [];
      if (groups.length <= 3) {
        return groups.map((group) => ({
          min: group.price,
          max: group.price,
          count: group.count,
          label: formatPrice(group.price)
        }));
      }

      const targetSize = items.length / 3;
      const bands = [];
      let currentGroups = [];
      let currentCount = 0;

      groups.forEach((group, index) => {
        currentGroups.push(group);
        currentCount += group.count;
        const remainingGroups = groups.length - index - 1;
        const remainingBands = 2 - bands.length;
        const canCloseBand =
          bands.length < 2 &&
          currentCount >= targetSize &&
          remainingGroups >= remainingBands;

        if (canCloseBand) {
          bands.push({
            min: currentGroups[0].price,
            max: currentGroups[currentGroups.length - 1].price,
            count: currentCount
          });
          currentGroups = [];
          currentCount = 0;
        }
      });

      if (currentGroups.length) {
        bands.push({
          min: currentGroups[0].price,
          max: currentGroups[currentGroups.length - 1].price,
          count: currentCount
        });
      }

      return bands.map((band, index) => {
        let label = `${formatPrice(band.min)}–${formatPrice(band.max)}`;
        if (band.min === band.max) label = formatPrice(band.min);
        else if (index === 0) label = `Up to ${formatPrice(band.max)}`;
        else if (index === bands.length - 1) label = `${formatPrice(band.min)} and above`;
        return { ...band, label };
      });
    };

    const setOptionCount = (button, count, showCount = true) => {
      const label = button.dataset.giftLabel || button.textContent.trim();
      const itemLabel = count === 1 ? "piece" : "pieces";
      button.dataset.giftLabel = label;
      const labelNode = document.createElement("span");
      labelNode.className = "bsk-gift-option__label";
      labelNode.textContent = label;
      button.replaceChildren(labelNode);
      if (showCount) {
        const countNode = document.createElement("small");
        countNode.className = "bsk-gift-option__count";
        countNode.textContent = `${count} ${itemLabel}`;
        button.append(countNode);
      }
      button.disabled = count === 0;
      button.hidden = count === 0 && button.dataset.giftValue !== "any";
      button.setAttribute("aria-label", showCount ? `${label}, ${count} ${itemLabel}` : label);
    };

    refreshStepOptions = (index) => {
      const step = steps[index];
      if (!step) return;
      const key = step.dataset.giftStep;
      const description = step.querySelector("p");

      if (key === "occasion") {
        step.querySelectorAll("[data-gift-value]").forEach((button) => {
          const count = collectMatches(
            { format: true, budget: true, personalized: true },
            { occasion: button.dataset.giftValue }
          ).length;
          setOptionCount(button, count, false);
        });
        if (description) description.textContent = "Choose the moment and we will shape the edit around it.";
        return;
      }

      if (key === "format") {
        const candidates = collectMatches({ format: true, budget: true, personalized: true });
        step.querySelectorAll("[data-gift-value]").forEach((button) => {
          const count = collectMatches(
            { budget: true, personalized: true },
            { format: button.dataset.giftValue }
          ).length;
          setOptionCount(button, count);
        });
        if (description) description.textContent = `${candidates.length} pieces match your first choice. Choose a format to narrow them down.`;
        return;
      }

      if (key === "budget") {
        const candidates = collectMatches({ budget: true, personalized: true });
        const options = step.querySelector("[data-budget-options]");
        const bands = buildBudgetBands(candidates);
        if (options) {
          const buttons = bands.map((band) => {
            const button = document.createElement("button");
            button.className = "bsk-gift-option";
            button.type = "button";
            button.dataset.giftValue = `budget-${band.min}-${band.max}`;
            button.dataset.giftLabel = band.label;
            button.dataset.giftMin = String(band.min);
            button.dataset.giftMax = String(band.max);
            button.setAttribute("aria-pressed", String(state.budget?.value === button.dataset.giftValue));
            setOptionCount(button, band.count);
            return button;
          });
          const anyButton = document.createElement("button");
          anyButton.className = "bsk-gift-option";
          anyButton.type = "button";
          anyButton.dataset.giftValue = "any";
          anyButton.dataset.giftLabel = "Any budget";
          anyButton.setAttribute("aria-pressed", String(state.budget?.value === "any"));
          setOptionCount(anyButton, candidates.length);
          options.replaceChildren(...buttons, anyButton);
        }
        if (description) {
          const prices = candidates.map((item) => Number(item.dataset.price || 0)).sort((a, b) => a - b);
          description.textContent = prices.length
            ? `${candidates.length} pieces match so far, from ${formatPrice(prices[0])} to ${formatPrice(prices[prices.length - 1])}.`
            : "No priced piece matches this combination. Go back and adjust a choice.";
        }
        return;
      }

      if (key === "personalized") {
        const candidates = collectMatches({ personalized: true });
        step.querySelectorAll("[data-gift-value]").forEach((button) => {
          const count = collectMatches({}, { personalized: button.dataset.giftValue }).length;
          setOptionCount(button, count);
        });
        if (description) description.textContent = `Choose whether the ${candidates.length} matching pieces should support personalization.`;
      }
    };

    const renderSelected = () => {
      selectedList.innerHTML = "";
      steps.forEach((step, index) => {
        const key = step.dataset.giftStep;
        if (!state[key]) return;
        const button = document.createElement("button");
        button.type = "button";
        button.dataset.editStep = String(index);
        button.textContent = `${state[key].label} ✎`;
        selectedList.appendChild(button);
      });
    };

    const renderResults = () => {
      let fallbackLevel = "";
      let matches = collectMatches();

      if (!matches.length) {
        fallbackLevel = "budget";
        matches = collectMatches({ budget: true });
      }
      if (!matches.length) {
        fallbackLevel = "personalization";
        matches = collectMatches({ budget: true, personalized: true });
      }
      if (!matches.length) {
        fallbackLevel = "format";
        matches = collectMatches({ budget: true, personalized: true, format: true });
      }
      if (!matches.length) {
        fallbackLevel = "catalog";
        matches = collectMatches({
          occasion: true,
          format: true,
          budget: true,
          personalized: true
        });
      }
      matches = matches.slice(0, 3);

      resultGrid.innerHTML = "";
      matches.forEach((item) => {
        const card = item.firstElementChild?.cloneNode(true);
        if (card) resultGrid.appendChild(card);
      });

      renderSelected();
      const occasionButton = finder.querySelector(
        `[data-gift-step="occasion"] [data-gift-value="${CSS.escape(state.occasion?.value || "")}"]`
      );
      if (allResultsLink && occasionButton?.dataset.targetUrl) {
        allResultsLink.href = occasionButton.dataset.targetUrl;
      }

      if (resultTitle) {
        resultTitle.textContent = fallbackLevel
          ? "The closest available alternatives"
          : "Our picks for you";
      }
      if (fallbackNote) fallbackNote.hidden = !fallbackLevel;
      results.hidden = false;
      if (empty) empty.hidden = true;
      pushEvent("gift_finder_completed", {
        gift_occasion: state.occasion?.value,
        gift_format: state.format?.value,
        gift_budget: state.budget?.value,
        gift_personalized: state.personalized?.value,
        result_count: matches.length,
        fallback_used: Boolean(fallbackLevel),
        fallback_level: fallbackLevel || "none"
      });
    };

    finder.addEventListener("click", (event) => {
      const option = event.target.closest("[data-gift-value]");
      const back = event.target.closest("[data-gift-back]");
      const resetButton = event.target.closest("[data-gift-reset]");
      const editButton = event.target.closest("[data-edit-step]");
      const removeBudget = event.target.closest("[data-remove-budget]");

      if (option) {
        const step = option.closest("[data-gift-step]");
        const key = step.dataset.giftStep;
        const stepIndex = steps.indexOf(step);
        clearStepsAfter(stepIndex);
        step.querySelectorAll("[data-gift-value]").forEach((button) => {
          button.setAttribute("aria-pressed", String(button === option));
        });
        state[key] = {
          value: option.dataset.giftValue,
          label: option.dataset.giftLabel || option.textContent.trim(),
          min: option.dataset.giftMin ? Number(option.dataset.giftMin) : null,
          max: option.dataset.giftMax ? Number(option.dataset.giftMax) : null
        };
        if (!started) {
          started = true;
          pushEvent("gift_finder_started");
        }
        updateUrl();
        if (activeStep < steps.length - 1) {
          setStep(activeStep + 1);
        } else {
          renderResults();
        }
      }

      if (back) setStep(activeStep - 1);
      if (resetButton) reset();
      if (editButton) setStep(Number(editButton.dataset.editStep));
      if (removeBudget) {
        delete state.budget;
        const budgetStep = finder.querySelector('[data-gift-step="budget"]');
        budgetStep?.querySelectorAll("[data-gift-value]").forEach((button) => {
          button.setAttribute("aria-pressed", "false");
        });
        setStep(2);
      }
    });

    const queryMap = {
      occasion: "gift_occasion",
      format: "gift_format",
      budget: "gift_budget",
      personalized: "gift_personalized"
    };
    const url = new URL(window.location.href);
    let restored = 0;
    const orderedKeys = ["occasion", "format", "budget", "personalized"];
    for (const key of orderedKeys) {
      const queryKey = queryMap[key];
      const value = url.searchParams.get(queryKey);
      if (!value) break;
      refreshStepOptions(restored);
      const button = finder.querySelector(
        `[data-gift-step="${key}"] [data-gift-value="${CSS.escape(value)}"]`
      );
      if (!button || button.disabled) break;
      button.setAttribute("aria-pressed", "true");
      state[key] = {
        value,
        label: button.dataset.giftLabel || button.textContent.trim(),
        min: button.dataset.giftMin ? Number(button.dataset.giftMin) : null,
        max: button.dataset.giftMax ? Number(button.dataset.giftMax) : null
      };
      restored += 1;
    }
    if (restored === steps.length) {
      started = true;
      renderResults();
    } else {
      setStep(restored);
    }
  };

  const initCorporate = (root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const projectSelect = root.querySelector("[data-project-select]");
    root.querySelectorAll("[data-project-card]").forEach((card) => {
      card.addEventListener("click", () => {
        root.querySelectorAll("[data-project-card]").forEach((item) => {
          item.classList.toggle("is-selected", item === card);
        });
        if (projectSelect) {
          projectSelect.value = card.dataset.projectValue;
          projectSelect.dispatchEvent(new Event("change", { bubbles: true }));
        }
        pushEvent("b2b_pathway_selected", {
          b2b_pathway: card.dataset.projectValue
        });
      });
    });

    const tabs = Array.from(root.querySelectorAll("[data-format-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-format-panel]"));
    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        const value = tab.dataset.formatTab;
        tabs.forEach((item) => {
          item.setAttribute("aria-pressed", String(item === tab));
        });
        panels.forEach((panel) => {
          panel.hidden = panel.dataset.formatPanel !== value;
        });
      });
    });

    root.querySelectorAll('a[href="#quote"]').forEach((link) => {
      link.addEventListener("click", () => pushEvent("b2b_quote_started"));
    });
    root.querySelectorAll('a[href*="wa.me"], a[data-whatsapp]').forEach((link) => {
      link.addEventListener("click", () => pushEvent("b2b_whatsapp_clicked"));
    });
    root.querySelector("[data-corporate-form]")?.addEventListener("submit", () => {
      pushEvent("b2b_quote_submitted");
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll("[data-gift-finder]").forEach(initGiftFinder);
    scope.querySelectorAll("[data-corporate-page]").forEach(initCorporate);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
